import isEqual from 'lodash/isEqual';

import type { Video, VideoVersion } from '@src/types.ts/story';
import type { VideoResourceMetadata } from '@src/types.ts/video';

export type LoadedVideoFreshness =
  | 'current'
  | 'fieldsChanged'
  | 'contentChanged';

/** Record fields a write can change without touching what the canvas renders. */
const RECORD_FIELDS = [
  'title',
  'isDownloadEnabled',
  'assignee',
  'reviewStatus',
  'isHidden',
  'isClientReady',
  'lastActionJson',
  'videoStatus',
  'videoFilePrimary',
  'thumbnail',
  'associatedVideos',
  'parentVideo',
  'versionId',
] as const;

export type LoadedVideo = Partial<Video> & { versionId?: string };
export type RecordFields = Partial<
  Pick<VideoVersion, (typeof RECORD_FIELDS)[number]>
>;

/**
 * PLAT-11911 — how a clip Creator Studio holds differs from Dato's head
 * version: not at all, only in record fields (a rename, download toggle,
 * publish, variant link…), or in what it renders.
 */
export function checkLoadedVideoAgainstHead(
  loaded: LoadedVideo,
  head: VideoVersion,
): LoadedVideoFreshness {
  if (head.versionId === loaded.versionId) return 'current';
  const sameContent =
    sameResources(loaded.resourcesMetadata, head.resourcesMetadata) &&
    isEqual(loaded.extraElementData ?? {}, head.extraElementData ?? {}) &&
    isEqual(loaded.clipJson ?? {}, head.clipJson ?? {});
  return sameContent ? 'fieldsChanged' : 'contentChanged';
}

export function pickRecordFields(video: LoadedVideo): RecordFields {
  const fields: Record<string, unknown> = {};
  for (const key of RECORD_FIELDS) fields[key] = video[key];
  return fields as RecordFields;
}

/**
 * The fields of `head` to write onto `loaded`: those still as they were in
 * `before`, so a local change made while the head was fetched stays.
 */
export function recordFieldsToPatch(
  loaded: LoadedVideo,
  before: RecordFields,
  head: VideoVersion,
): RecordFields {
  const patch: Record<string, unknown> = {};
  for (const key of RECORD_FIELDS) {
    if (loaded[key] === before[key]) patch[key] = head[key];
  }
  return patch as RecordFields;
}

/**
 * The parent is another record: its rename, publish or sibling variants don't
 * change this clip's version. The sibling ids matter, since adding a variant
 * writes the parent's list back from this copy.
 */
export function parentChanged(
  loaded: Video['parentVideo'],
  head: Video['parentVideo'],
): boolean {
  if (loaded?.id !== head?.id) return true;
  const ids = (parent: Video['parentVideo']) =>
    (parent?.associatedVideos ?? [])
      .map(v => v.id)
      .sort()
      .join();
  return ids(loaded) !== ids(head);
}

/**
 * Resource files are re-uploaded under a new timestamped key on every save,
 * so the md5 is what identifies their content. A clip without a composition
 * snapshot keeps it inline, which this can't compare.
 */
function sameResources(
  a: Video['resourcesMetadata'],
  b: Video['resourcesMetadata'],
): boolean {
  if (!a?.videoSourceSnapshot || !b?.videoSourceSnapshot) return false;
  return sameEntries(a, b);
}

function sameEntries(a: object, b: object): boolean {
  const aEntries = a as Record<string, unknown>;
  const bEntries = b as Record<string, unknown>;
  const keys = new Set([...Object.keys(aEntries), ...Object.keys(bEntries)]);
  for (const key of keys) {
    const left = aEntries[key];
    const right = bEntries[key];
    if (!left || !right || typeof left !== 'object') return false;
    if (typeof right !== 'object') return false;
    const same = isResourceFile(left)
      ? isResourceFile(right) && sameFile(left, right)
      : // Karaoke: one file per language.
        !isResourceFile(right) && sameEntries(left, right);
    if (!same) return false;
  }
  return true;
}

function isResourceFile(
  value: object,
): value is Partial<VideoResourceMetadata> {
  return 'fileKey' in value || 'md5Hash' in value || 'url' in value;
}

function sameFile(
  a: Partial<VideoResourceMetadata>,
  b: Partial<VideoResourceMetadata>,
): boolean {
  return (
    (!!a.fileKey && a.fileKey === b.fileKey) ||
    (!!a.md5Hash && a.md5Hash === b.md5Hash)
  );
}
